/**
 * Study Mode "Mix" — domain catalog + live mastery aggregation.
 *
 * The five AZ-104 domains below are a stable DESIGN catalog (name key, icon,
 * gradient, exam-blueprint weight). Their mastery is NOT hard-coded: it is
 * aggregated from the real per-topic mastery returned by the dashboard
 * endpoint (`GET /api/auth/me/dashboard` → `topic_mastery[]`), matched into
 * each domain via the topic aliases in `resolveTopic.ts`.
 *
 * A domain with no answered questions yet reports `mastery: null` so the UI
 * can show "not started" rather than a misleading 0%.
 */

import type { GlyphIcon } from '../../ui/primitives';
import type { TopicMastery } from '../../../types/admin';
import { topicMatchesDomain } from './resolveTopic';

/** Static per-domain design metadata (no live figures). */
export interface DomainMeta {
  /** Stable key used for i18n lookups under `study.mix.domains.*` and alias matching. */
  key: string;
  /** Two-digit catalog code. */
  code: string;
  icon: GlyphIcon;
  gradient: [string, string];
  /** Exam blueprint weight (%). */
  weight: number;
}

/** A domain enriched with the learner's live mastery. */
export interface DomainStat extends DomainMeta {
  /** Accuracy over answered questions in this domain (0..100), or null if none answered. */
  mastery: number | null;
  /** Questions answered across the domain's topics. */
  answered: number;
  /** Total questions available across the domain's topics. */
  total: number;
}

/** The five AZ-104 domains (mirrors the exam blueprint + design reference). */
export const DOMAIN_META: DomainMeta[] = [
  { key: 'identity', code: '01', icon: 'shield', gradient: ['#2563EB', '#4F46E5'], weight: 22 },
  { key: 'storage', code: '02', icon: 'database', gradient: ['#0891B2', '#2563EB'], weight: 18 },
  { key: 'compute', code: '03', icon: 'cpu', gradient: ['#7C3AED', '#2563EB'], weight: 24 },
  { key: 'networking', code: '04', icon: 'network', gradient: ['#F59E0B', '#D97706'], weight: 16 },
  { key: 'monitor', code: '05', icon: 'activity', gradient: ['#0D9488', '#16A34A'], weight: 20 },
];

/**
 * Fold the live `topic_mastery[]` into the five design domains. Mastery is the
 * answered-accuracy across a domain's matching topics (weighted by volume);
 * null when the learner has not answered anything in the domain yet.
 */
export function buildDomainStats(topicMastery: TopicMastery[] | undefined): DomainStat[] {
  const tm = topicMastery ?? [];
  return DOMAIN_META.map((d) => {
    const matches = tm.filter((t) => topicMatchesDomain(t.topic, d.key));
    const answered = matches.reduce((s, t) => s + (t.answered || 0), 0);
    const correct = matches.reduce((s, t) => s + (t.correct || 0), 0);
    const total = matches.reduce((s, t) => s + (t.total_questions || 0), 0);
    return {
      ...d,
      answered,
      total,
      mastery: answered > 0 ? Math.round((correct / answered) * 100) : null,
    };
  });
}

/** Lowest-mastery started domain — the natural focus for a smart session. */
export function weakestDomain(stats: DomainStat[]): DomainStat | null {
  const started = stats.filter((d) => d.mastery !== null);
  if (started.length === 0) return null;
  return started.reduce((low, d) => ((d.mastery as number) < (low.mastery as number) ? d : low));
}
